/** @jsx jsx */
import { Button, Grid, Icon, IconButton, useMediaQuery } from "@cd2/components"
import { P } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import React, { useRef, useState } from "react"
import { Link, useHistory } from "react-router-dom"
import { icons } from "../constants/icons"
import { buildRoute, eRoutes } from "../routes"
import { useWebsiteUser } from "./WebsiteProvider"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { IRecord } from "../types/schema"
import { useApi } from "../hooks/useApi"
import { IBranchSettings } from "../utils/useSettings"
import { brandColors } from "./EOSThemeProvider"
import { differenceInSeconds, format, formatDistanceToNowStrict } from "date-fns"
import { PopOutPanel } from "./PopOutPanel"
import { GenericIcon } from "../assets/svgComponents/GenericIcon"
import {
  HolidayRequestSourceTypes,
  HolidayRequestStatuses,
} from "../Pages/HR/HolidayRequests/Manage/HolidayRequestsForm"
import { unique } from "@cd2/tools"

type INotification = IRecord<
  "notifications",
  "heading" | "body" | "route" | "unread" | "created_at"
>

// Some users have tens of thousands of unread notifications, so only fetch one more than we show
const NOTIFICATION_DISPLAY_LIMIT = 30

export function NotificationsBell({ type }: { type?: "customer" }) {
  const isSmallDevice = useMediaQuery({ maxWidth: 450 })
  const [notifications, setNotifications] = useState<INotification[] | null>(null)
  const { currentUser } = useWebsiteUser()
  const skeem = window.skeem
  const history = useHistory()

  useAsyncEffect(async () => {
    try {
      await fetchNotifications()
    } catch (e) {
      console.error("Error fetching notifications", e)
    }
    try {
      await startNotificationPolling()
    } catch (e) {
      console.error("Error starting notification polling", e)
    }
  }, [])

  async function fetchNotifications() {
    const userWithNotifications = await skeem.fetch2<{ received_notifications: INotification[] }>({
      first: {
        name: "skeem_users",
        attributes: [
          {
            name: "received_notifications",
            attributes: ["heading", "body", "route", "created_at", "unread"],
            sort: { by: "created_at", direction: "desc" },
            filter: { matches: { unread: true } },
            paginate: { size: NOTIFICATION_DISPLAY_LIMIT + 1 },
          },
        ],
        filter: { matches: { id: currentUser.id } },
      },
    })
    const newCount = (userWithNotifications?.received_notifications ?? []).length
    const oldCount = (notifications ?? []).length
    setNotifications(userWithNotifications?.received_notifications ?? [])
    return newCount > oldCount
  }

  async function handleNotificationClick(notification: INotification) {
    await skeem.mutate({ notifications: { update: { id: notification.id, unread: false } } })
    if (notification.route) {
      history.push(notification.route)
    }
    await fetchNotifications()
  }

  async function handleToggleAsRead(notification: INotification) {
    await skeem.mutate({
      notifications: { update: { id: notification.id, unread: !notification.unread } },
    })
    await fetchNotifications()
  }

  async function startNotificationPolling() {
    const user = await skeem.fetchMe<IRecord<"skeem_users", "userAccountType">>(["userAccountType"])
    if (!user) {
      return
    }
    const isBranchUser = currentUser.userAccountType === "branch"
    const minuteDuration = 60 * 1000
    const pollDuration = isBranchUser ? 3 * minuteDuration : 5 * minuteDuration
    pollForNotifications(pollDuration)
  }

  const pollRef = useRef<any>(null)

  async function pollForNotifications(pollDuration: number) {
    await new Promise(resolve => {
      if (pollRef.current) {
        clearTimeout(pollRef.current)
      }
      pollRef.current = setTimeout(() => {
        fetchNotifications().finally(() => resolve(true))
      }, pollDuration)
    }).catch(e => {
      console.error("Error polling for notifications", e, pollDuration)
    })
    pollForNotifications(pollDuration)
  }

  if (!notifications) {
    if (type == "customer") {
      return (
        <Button buttonStyle={"text"} extendTheme={{ padding: 4 }}>
          <Icon icon={icons.notificationBellS} color={brandColors.darkMint} size={28} />
        </Button>
      )
    }
    return <IconButton icon={icons.notificationBell} buttonColor="white" disabled square />
  }

  return (
    <PopOutPanel
      title="Notifications"
      width={isSmallDevice ? 340 : 440}
      maxWidth={isSmallDevice ? 340 : 440}
      content={({ setIsOpen }) => (
        <React.Fragment>
          {notifications.length === 0 && <P>You have no unread notifications.</P>}
          <div
            css={css`
              padding: 4px;
              max-height: 400px;
            `}
          >
            <Grid gap={4}>
              {notifications.slice(0, NOTIFICATION_DISPLAY_LIMIT).map(notification => (
                <div
                  key={notification.id}
                  onClick={() => {
                    handleNotificationClick(notification)
                    setIsOpen(false)
                  }}
                  css={css`
                    min-width: 100%;
                    padding: 16px 8px;
                    transition: background-color 0.3s;
                    cursor: pointer;
                    background-color: ${notification.unread ? "#f5f5f5" : "white"};
                    opacity: ${notification.unread ? 1 : 0.6};
                  `}
                >
                  <div
                    css={css`
                      position: relative;
                    `}
                  >
                    <div
                      css={css`
                        :first-child {
                          white-space: pre-wrap;
                        }
                      `}
                    >
                      <P size="small">
                        <b>{notification.heading}</b>
                        {notification.body && ` - ${notification.body}`}
                      </P>
                      {notification.created_at && (
                        <P size="small" color="#999">
                          {differenceInSeconds(new Date(), new Date(notification.created_at)) < 30
                            ? "Just now"
                            : formatDistanceToNowStrict(new Date(notification.created_at), {
                                addSuffix: true,
                              })}
                        </P>
                      )}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        position: absolute;
                        bottom: -4px;
                        right: -4px;
                      `}
                    >
                      {notification.route ? (
                        <div>
                          <Link to={notification.route}>
                            <IconButton
                              icon={icons.external}
                              buttonStyle="text"
                              color={brandColors.night}
                              iconSize={12}
                            />
                          </Link>
                        </div>
                      ) : (
                        <div />
                      )}
                      <div
                        onClick={e => {
                          e.stopPropagation()
                          handleToggleAsRead(notification)
                        }}
                        css={css`
                          opacity: ${notification.unread ? 0.2 : 1};
                          transition: opacity 0.3s;

                          &:hover {
                            opacity: 1;
                          }
                        `}
                      >
                        <IconButton
                          icon={icons.check}
                          buttonStyle="text"
                          color={brandColors.night}
                          iconSize={12}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Grid>
          </div>
        </React.Fragment>
      )}
    >
      {({ isOpen, setIsOpen }) =>
        type === "customer" ? (
          <div
            css={css`
              position: relative;
            `}
          >
            <Button
              onClick={() => setIsOpen(!isOpen)}
              buttonStyle={"text"}
              extendTheme={{ padding: 4 }}
            >
              <Icon icon={icons.notificationBellS} color={brandColors.darkMint} size={28} />
            </Button>
            {notifications.filter(n => n.unread).length > 0 && (
              <div
                css={css`
                  position: absolute;
                  z-index: 2;
                  top: 4px;
                  pointer-events: none;
                  right: 4px;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background-color: red;
                  font-size: 7px;
                  color: white;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                {notifications.filter(n => n.unread).length > NOTIFICATION_DISPLAY_LIMIT
                  ? `${NOTIFICATION_DISPLAY_LIMIT}+`
                  : notifications.filter(n => n.unread).length}
              </div>
            )}
          </div>
        ) : (
          <div
            css={css`
              position: relative;
            `}
          >
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              icon={icons.notificationBell}
              buttonColor={undefined}
              buttonStyle={"text"}
              color="white"
              square
            />

            {notifications && (
              <React.Fragment>
                {notifications.filter(n => n.unread).length > 0 && (
                  <div
                    css={css`
                      position: absolute;
                      z-index: 2;
                      top: 4px;
                      pointer-events: none;
                      right: 4px;
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      background-color: ${brandColors.mint};
                    `}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        )
      }
    </PopOutPanel>
  )
}

export function useNotification() {
  const api = useApi()
  const skeem = window.skeem

  async function sendNotification({
    to,
    heading,
    body,
    route,
  }: {
    to: {
      emails?: string[]
      phoneNumbers?: string[]
      userIds?: string[]
    }
    heading: string
    body: string
    route?: string
  }) {
    if (!to.emails?.length && !to.phoneNumbers?.length && !to.userIds?.length) {
      throw new Error("At least one email, phone or user is required")
    }
    if (!heading) {
      throw new Error("Heading is required")
    }
    if (!body) {
      throw new Error("Body is required")
    }
    await api.sendNotification({
      to,
      content: {
        heading: heading,
        body: body,
        route: route,
      },
    })
  }

  async function sendRmaApprovedNotification(rmaId: string) {
    if (!rmaId) {
      console.log("sendRmaApprovedNotification", "No RMA ID provided")
      return
    }
    const contactAttributes = ["email", "name", "user", "is_default"]
    const rma = await skeem.fetch2<
      IRecord<"rmas", "identifier"> & {
        customer?: IRecord<"customers"> & {
          contacts: IRecord<"contacts", "is_default" | "email" | "name" | "user">[]
        }
        contact?: IRecord<"contacts", "email" | "name" | "user">
      }
    >({
      first: {
        name: "rmas",
        attributes: [
          "identifier",
          { name: "customer", attributes: [{ name: "contacts", attributes: contactAttributes }] },
          { name: "contact", attributes: contactAttributes },
        ],
        matches: { id: rmaId },
      },
    })

    if (!rma || (!rma.customer && !rma.contact)) {
      console.log("sendRmaApprovedNotification", "No RMA found", "No customer AND No contact found")
      return
    }
    const contact = rma.contact ? rma.contact : rma.customer?.contacts.find(c => c.is_default)
    if (!contact?.email && !contact?.user) {
      console.log("sendRmaApprovedNotification", "No contact email or user found")
      return
    }
    const recipient = contact.user ? { userIds: [contact.user.id] } : { emails: [contact.email] }
    console.log("NOTIFY RMA RAISED", recipient)
    return sendNotification({
      to: recipient,
      heading: "RMA Approved",
      body: `Your RMA ${rma.identifier} has been approved`,
      route: buildRoute(eRoutes["/rma/:id"], { id: rma.id }),
    })
  }

  async function sendNewTradeApplicationNotification(tradeApplicationId: string) {
    const tradeApp = await skeem.fetch2<
      IRecord<"trade_applications", "company_name"> & {
        company_settings: IBranchSettings
      }
    >({
      first: {
        name: "trade_applications",
        attributes: [
          "company_name",
          { as: "company_settings", value: { path: ["user", "branch", "settings"] } },
        ],
        filter: { matches: { id: tradeApplicationId } },
      },
    })
    const emails = tradeApp.company_settings.notificationEmailsNewTradeApplication || []

    if (emails.length === 0) {
      return
    }

    await sendNotification({
      to: { emails },
      heading: "New Trade Account Application",
      body: `A new trade account application has been submitted for company ${tradeApp.company_name}`,
      route: buildRoute(eRoutes["/customer-support/trade-applications/:id"], {
        id: tradeApp.id,
      }),
    })
  }

  async function sendTradeApplicationActionedNotification(
    tradeApplicationId: string,
    status: "approved" | "rejected",
    reason?: string,
  ) {
    const tradeApp = await skeem.fetch2<
      IRecord<"trade_applications", "company_name"> & {
        company_settings: IBranchSettings
      }
    >({
      first: {
        name: "trade_applications",
        attributes: [
          "company_name",
          { as: "company_settings", value: { path: ["user", "branch", "settings"] } },
        ],
        filter: { matches: { id: tradeApplicationId } },
      },
    })
    const emails = tradeApp.company_settings.notificationEmailsActionedTradeApplications || []
    if (emails.length === 0) {
      return
    }

    const fStatus = {
      approved: "Approved",
      rejected: "Rejected",
    }[status]

    let message = `A trade account application has been ${fStatus} for company ${tradeApp.company_name}`
    if (status === "rejected" && reason) {
      message += ` with reason: ${reason}`
    }
    console.log("SEND APP ACTIONED EMAIL", message)
    await sendNotification({
      to: { emails },
      heading: `Trade Account Application was ${fStatus}`,
      body: message,
      route: buildRoute(eRoutes["/customer-support/trade-applications/:id"], {
        id: tradeApp.id,
      }),
    })
  }

  async function sendTradeApplicationBranchApproved(
    tradeApplicationId: string,
    accountsEmails: string[],
  ) {
    const tradeApp = await skeem.fetch2<
      IRecord<"trade_applications", "company_name"> & {
        branch: IRecord<"branches", "name"> & { settings: IBranchSettings }
      }
    >({
      first: {
        name: "trade_applications",
        attributes: [
          "company_name",
          {
            as: "branch",
            value: { path: ["user", { name: "branch", attributes: ["settings", "name"] }] },
          },
        ],
        filter: { matches: { id: tradeApplicationId } },
      },
    })
    if (accountsEmails.length === 0) {
      return
    }
    await sendNotification({
      to: { emails: accountsEmails },
      heading: `${tradeApp.branch.name} Trade Account Application approved`,
      body: `${tradeApp.branch.name} branch has approved a trade account application for ${tradeApp.company_name}.`,
      route: buildRoute(eRoutes["/customer-support/trade-applications/:id"], {
        id: tradeApp.id,
      }),
    })
  }

  async function sendNewCustomerUserNotification(userId: string) {
    const customerUser = await skeem.fetch2<
      IRecord<
        "skeem_users",
        "email" | "first_name" | "last_name" | "hasExistingTradeAccount" | "companyName"
      > & { branch: IRecord<"branches", "name"> & { settings: IBranchSettings } }
    >({
      first: {
        name: "skeem_users",
        attributes: [
          "email",
          "first_name",
          "last_name",
          "hasExistingTradeAccount",
          "companyName",
          { name: "branch", attributes: ["name", "settings"] },
        ],
        filter: { matches: { id: userId } },
      },
    })
    const emails = customerUser.branch.settings.notificationEmailsNewCustomerAccount || []

    if (emails.length === 0) {
      return
    }

    await sendNotification({
      to: { emails },
      heading: "New Customer Account",
      body: `A ${
        customerUser.hasExistingTradeAccount
          ? "customer user for an existing trade account"
          : "new customer user"
      } has been created for ${customerUser.branch.name}: ${customerUser.email} (${
        customerUser.companyName
      })`,
      route: buildRoute(eRoutes["/customer-support/pending-accounts/:id"], {
        id: customerUser.id,
      }),
    })
  }

  async function sendOrderRequestNotification(orderRequestId: string) {
    const orderRequest = await skeem.fetch2<
      IRecord<"orderRequests", "identifier" | "received_by_branch"> & {
        createdByBranchName: string
        receivingBranchSettings: IBranchSettings
      }
    >({
      first: {
        name: "orderRequests",
        attributes: [
          "identifier",
          { as: "createdByBranchName", value: { path: ["created_by_branch", "name"] } },
          { as: "receivingBranchSettings", value: { path: ["received_by_branch", "settings"] } },
        ],
        filter: { matches: { id: orderRequestId } },
      },
    })
    const emails = orderRequest.receivingBranchSettings?.notificationEmailsOrderRequestCreated || []

    if (emails.length === 0) {
      return
    }

    await sendNotification({
      to: { emails },
      heading: "New Order Request",
      body: `${orderRequest.identifier} has been created by ${orderRequest.createdByBranchName}.`,
      route: buildRoute(eRoutes["/order-requests/:id/preview"], {
        id: orderRequest.id,
      }),
    })
  }

  async function sendOrderRequestOrderExported(orderId: string) {
    const order = await skeem.fetch2<
      IRecord<"orders"> & {
        orderRequest?: IRecord<"orderRequests", "identifier"> & {
          created_by_user?: IRecord<"skeem_users", "skeem_email">
        }
      }
    >({
      first: {
        name: "orders",
        attributes: [
          {
            name: "orderRequest",
            attributes: ["identifier", { name: "created_by_user", attributes: ["skeem_email"] }],
          },
        ],
        filter: { matches: { id: orderId } },
      },
    })
    if (!order.orderRequest) {
      console.warn("Order does not have an associated order request")
      return
    }
    if (!order.orderRequest.created_by_user) {
      console.error("Order request does not have an associated user")
      return
    }

    const email = order.orderRequest.created_by_user.skeem_email

    if (!email) {
      return
    }

    await sendNotification({
      to: { emails: [email] },
      heading: "Your order request has been processed",
      body: `An order for your order request ${order.orderRequest.identifier} has been processed for packing`,
      route: buildRoute(eRoutes["/order-requests/:id/preview"], {
        id: order.orderRequest.id,
      }),
    })
  }

  async function sendOrderCreatedForOrderRequestNotification(orderRequestId: string) {
    const orderRequest = await skeem.fetch2<
      IRecord<"orderRequests", "identifier" | "received_by_branch"> & {
        receivingBranchName: string
        createdBranchSettings: IBranchSettings
      }
    >({
      first: {
        name: "orderRequests",
        attributes: [
          "identifier",
          { as: "receivingBranchName", value: { path: ["received_by_branch", "name"] } },
          { as: "createdBranchSettings", value: { path: ["created_by_branch", "settings"] } },
        ],
        filter: { matches: { id: orderRequestId } },
      },
    })
    const emails =
      orderRequest.createdBranchSettings?.notificationEmailsOrderRequestAssignedToOrder || []

    await sendNotification({
      to: { emails },
      heading: `Order Created For Order Request`,
      body: `An order has been created for your order request ${orderRequest.identifier} by ${orderRequest.receivingBranchName}`,
      route: buildRoute(eRoutes["/order-requests/:id/preview"], {
        id: orderRequest.id,
      }),
    })
  }

  async function sendCustomerAcceptedQuoteNotification(quoteId: string) {
    const quote = await skeem.fetch2<
      IRecord<"quotes", "identifier"> & { branchSettings: IBranchSettings } & {
        customerName: string
      }
    >({
      first: {
        name: "quotes",
        attributes: [
          "identifier",
          { as: "customerName", value: { path: ["customer", "name"] } },
          { as: "branchSettings", value: { path: ["customer", "branch", "settings"] } },
        ],
        filter: { matches: { id: quoteId } },
      },
    })
    if (!quote) {
      throw new Error("Quote not found")
    }
    if (!quote.customerName) {
      throw new Error("Customer not found")
    }
    if (!quote.branchSettings) {
      throw new Error("Quote has no branch settings")
    }
    const emails = quote.branchSettings.notificationEmailsCustomerQuoteAccepted
    if (!emails || emails.length === 0) {
      console.warn("No notification emails for customer job created")
      return
    }

    await sendNotification({
      to: { emails },
      heading: "Customer accepted a quote",
      body: `${quote.identifier} has been accepted by ${quote.customerName}`,
      route: buildRoute(eRoutes["/quotes/:id/preview"], {
        id: quote.id,
      }),
    })
  }

  async function sendCustomerOrderCreatedNotification(orderId: string) {
    const order = await skeem.fetch2<
      IRecord<"orders", "identifier"> & {
        quote?: IRecord<"quotes", "identifier"> & {
          job?: IRecord<"jobs"> & { user: IRecord<"skeem_users", "skeem_email"> }
        }
      }
    >({
      first: {
        name: "orders",
        attributes: [
          "identifier",
          {
            name: "quote",
            attributes: [
              "identifier",
              { name: "job", attributes: [{ name: "user", attributes: ["skeem_email"] }] },
            ],
          },
        ],
        filter: { matches: { id: orderId } },
      },
    })
    if (!order) {
      throw new Error("Order not found")
    }
    const email = order?.quote?.job?.user.skeem_email
    if (!email) {
      return
    }
    if (order.quote) {
      await sendNotification({
        to: { emails: [email] },
        heading: "Order has been generated from a quote",
        body: `${order.identifier} has been generated from quote ${order.quote.identifier}`,
        route: buildRoute(eRoutes["/orders/:id/preview"], {
          id: order.id,
        }),
      })
    } else {
      await sendNotification({
        to: { emails: [email] },
        heading: "New Order created",
        body: `${order.identifier} has been created`,
        route: buildRoute(eRoutes["/orders/:id/preview"], {
          id: order.id,
        }),
      })
    }
  }

  async function sendCommentOnJobNotification(jobId: string, sendTo: "branch" | "customer") {
    const job = await skeem.fetch2<
      IRecord<"jobs", "reference"> & { branchSettings: IBranchSettings } & {
        userEmail: string
        orderUserEmail?: string
        quoteUserEmail?: string
      }
    >({
      first: {
        name: "jobs",
        attributes: [
          "reference",
          { as: "userEmail", value: { path: ["user", "skeem_email"] } },
          { as: "branchSettings", value: { path: ["customer", "branch", "settings"] } },
          { as: "orderUserEmail", value: { path: ["order", "user", "skeem_email"] } },
          { as: "quoteUserEmail", value: { path: ["quote", "user", "skeem_email"] } },
        ],
        filter: { matches: { id: jobId } },
      },
    })
    if (!job) {
      throw new Error("Job not found")
    }
    if (!job.branchSettings) {
      throw new Error("Job has no branch settings")
    }

    let emails: string[] = []

    if (sendTo === "branch") {
      const branchUserEmail = job.orderUserEmail || job.quoteUserEmail
      if (branchUserEmail) {
        emails.push(branchUserEmail)
      }
      if (job.branchSettings.notificationEmailsCustomerJobComment.length > 0) {
        emails.push(...job.branchSettings.notificationEmailsCustomerJobComment)
      }
      emails = unique(emails)
      if (!emails || emails.length === 0) {
        console.warn("No notification emails for branch for customer job comment")
        return
      }
    }
    if (sendTo === "customer") {
      emails = [job.userEmail]
      if (!emails || emails.length === 0) {
        console.warn("No notification emails for customer for customer job comment")
        return
      }
    }

    await sendNotification({
      to: { emails },
      heading: "Comment on job",
      body: `Job: ${job.reference} has a new comment`,
      route: buildRoute(eRoutes["/jobs/:id/preview"], {
        id: job.id,
      }),
    })
  }

  async function sendNewCustomerRequestNotification(jobId: string) {
    const job = await skeem.fetch2<
      IRecord<"jobs", "identifier"> & { branchSettings: IBranchSettings }
    >({
      first: {
        name: "jobs",
        attributes: [
          "identifier",
          { as: "branchSettings", value: { path: ["customer", "branch", "settings"] } },
        ],
        filter: { matches: { id: jobId } },
      },
    })
    if (!job) {
      throw new Error("Job not found")
    }
    if (!job.branchSettings) {
      throw new Error("Job has no branch settings")
    }
    const emails = job.branchSettings.notificationEmailsCustomerJobCreated
    if (!emails || emails.length == 0) {
      console.warn("No notification emails for customer job created")
      return
    }

    await sendNotification({
      to: { emails },
      heading: "Customer request created",
      body: "A new customer request has been created",
      route: buildRoute(eRoutes["/jobs/:id/preview"], {
        id: job.id,
      }),
    })
  }

  async function sendNotificationHolidayRequestStatusChange(requestId: string) {
    const holidayRequest = await skeem.fetch2<
      IRecord<"holidayRequests", "status" | "startDate" | "endDate"> & {
        user: IRecord<"skeem_users">
      }
    >({
      first: {
        name: "holidayRequests",
        attributes: ["status", { name: "user" }, "startDate", "endDate"],
        filter: { matches: { id: requestId } },
      },
    })
    if (!holidayRequest) {
      throw new Error("Request not found")
    }
    if (!holidayRequest.user) {
      console.warn("No user to notify")
      return
    }
    const startDate = format(new Date(holidayRequest.startDate!), "iii do LLLL yyyy")
    const endDate = format(new Date(holidayRequest.endDate!), "iii do LLLL yyyy")
    await sendNotification({
      to: { userIds: [holidayRequest.user.id] },
      heading: `Time off request ${holidayRequest.status}`,
      body: `Your time off request from ${startDate} to ${endDate} has been ${
        HolidayRequestStatuses.APPROVED === holidayRequest.status ? "approved" : "declined"
      }`,
      route: buildRoute(eRoutes["/hr/requests/:id"], {
        id: holidayRequest.id,
      }),
    })
  }

  async function sendNotificationHolidayRequestCreated(requestId: string) {
    const holidayRequest = await skeem.fetch2<
      IRecord<"holidayRequests", "type" | "sourceType"> & {
        branchSettings?: IBranchSettings
        user: IRecord<"skeem_users", "name"> & {
          managedByHrUsers: IRecord<"skeem_users", "skeem_email">[]
        }
      }
    >({
      first: {
        name: "holidayRequests",
        attributes: [
          { as: "branchSettings", value: { path: ["user", "branch", "settings"] } },
          {
            name: "user",
            attributes: ["name", { name: "managedByHrUsers", attributes: ["skeem_email"] }],
          },
          "type",
          "sourceType",
        ],
        filter: { matches: { id: requestId } },
      },
    })
    if (!holidayRequest) {
      throw new Error("Request not found")
    }
    if (!holidayRequest.branchSettings) {
      console.warn("Missing required branch settings")
      return
    }

    const emails =
      holidayRequest.user.managedByHrUsers.length > 0
        ? holidayRequest.user.managedByHrUsers.map(x => x.skeem_email)
        : holidayRequest.branchSettings.notificationEmailsHolidayRequests ?? []

    if (emails.length === 0) {
      console.warn("No emails to notify of new holiday request")
      return
    }

    if (holidayRequest.sourceType === HolidayRequestSourceTypes.ABSENCE) {
      await sendNotification({
        to: { emails },
        heading: `New sick leave request from ${holidayRequest.user.name}`,
        body: `${holidayRequest.user.name} as submitted a sick leave request, it was automatically approved.`,
        route: buildRoute(eRoutes["/hr/manage-requests/:id"], {
          id: holidayRequest.id,
        }),
      })
    } else {
      await sendNotification({
        to: { emails },
        heading: `New time off request from ${holidayRequest.user.name}`,
        body: `${holidayRequest.user.name} as sent a new time off request`,
        route: buildRoute(eRoutes["/hr/manage-requests/:id"], {
          id: holidayRequest.id,
        }),
      })
    }
  }

  async function sendNotificationUserAssignedToCustomer(userId: string) {
    if (!userId) {
      console.error("No user id supplied")
      return
    }
    const user = await skeem.fetch2<IRecord<"skeem_users", "customer">>({
      first: { name: "skeem_users", attributes: ["customer"], matches: { id: userId } },
    })
    if (!user.customer) {
      console.error("No customer assigned to user")
      return
    }
    await sendNotification({
      to: { userIds: [user.id] },
      heading: "Your account has been approved",
      body: "Your FOS account has been linked to a customer account. You can now log in to view and create Quotes and Orders.",
    })
  }

  return {
    sendNotification,
    sendOrderRequestNotification,
    sendCustomerOrderCreatedNotification,
    sendNewCustomerRequestNotification,
    sendCustomerAcceptedQuoteNotification,
    sendCommentOnJobNotification,
    sendOrderCreatedForOrderRequestNotification,
    sendOrderRequestOrderExported,
    sendNewCustomerUserNotification,
    sendNewTradeApplicationNotification,
    sendTradeApplicationActionedNotification,
    sendNotificationHolidayRequestStatusChange,
    sendNotificationHolidayRequestCreated,
    sendNotificationUserAssignedToCustomer,
    sendTradeApplicationBranchApproved,
    sendRmaApprovedNotification,
  }
}
